import { Flex, Text } from "@chakra-ui/react";
import {
	AccordionItem,
	AccordionButton,
	AccordionPanel,
	AccordionIcon,
} from "@chakra-ui/react";
import { AlertCircle } from "lucide-react";
import {
	NewOrderEarSide,
	useNewOrderStore,
} from "../../../stores/order.store";
import { useEarmoldVariants } from "../../../hooks/earmolds.hooks";
import Tooltip from "../../../components/Tooltip";
import CircleHelpIcon from "../../../components/icons/CircleHelpIcon";
import { EarSectionFormSection } from "./EarSectionFormSection";
import type { TSelectDropdownOption } from "../../../components/inputs/SelectDropdown";
import { useFile } from "../../../hooks/file.hooks";
import type { Variant } from "../../../types/earmold.types";
import { getVariantMismatchInfo } from "./getVariantMismatchInfo";

export const EarSectionForm = ({ side }: { side: NewOrderEarSide }) => {
	const { earmold } = useNewOrderStore();
	const { data: variants } = useEarmoldVariants(earmold);

	if (!Array.isArray(variants)) {
		return <></>;
	}
	return (
		<>
			{variants.map((variant, index) => (
				<VariantAccordionItem
					key={variant.uuid}
					variant={variant}
					index={index}
					side={side}
				/>
			))}
		</>
	);
};

const VariantAccordionItem = ({
	variant,
	index,
	side,
}: {
	variant: Variant;
	index: number;
	side: NewOrderEarSide;
}) => {
	const { showError, toggleFormIndex, autoFormIndex } = useNewOrderStore();
	const { getVariantValue, setVariantValue } = useNewOrderStore();
	const { isDisabled, getForm } = useNewOrderStore();
	const variantFileId =
		variant.fileId ??
		(variant.file && typeof variant.file === "object" && "id" in variant.file
			? variant.file.id
			: null);
	const value = getVariantValue(side, variant.id.toString());
	const label = value?.label;
	const oppositeSide =
		side === NewOrderEarSide.LEFT ? NewOrderEarSide.RIGHT : NewOrderEarSide.LEFT;
	const oppositeValue = getVariantValue(oppositeSide, variant.id.toString());
	const { isDifferentBetweenSides } = getVariantMismatchInfo(value, oppositeValue);
	const isValueMissing = value === undefined || value === null;
	const hasOptionEnabled = variant.options.data.some(
		(option) => isDisabled(variant, option, getForm(side)) === false,
	);
	const { data: variantFile } = useFile({ id: variantFileId });
	const variantImageUrl =
		variantFile && typeof variantFile.url === "string"
			? variantFile.url
			: variant.file &&
					typeof variant.file === "object" &&
					"url" in variant.file &&
					typeof variant.file.url === "string"
				? variant.file.url
				: null;
	const variantImageAlt =
		variantFile && typeof variantFile.fileName === "string"
			? variantFile.fileName
			: variant.label;

	return (
		<AccordionItem>
			{({ isExpanded }) => (
				<>
					<AccordionButton
						onClick={() => {
							if (hasOptionEnabled === false) return;

							toggleFormIndex(index + 1);
						}}
					>
						<Flex justifyContent="space-between" w="full">
							<Flex alignItems="center" pb={2} gap={2}>
								{showError && hasOptionEnabled && isValueMissing && (
									<AlertCircle className="w-4 h-4 text-red-500" />
								)}
								<Text
									lineHeight="21px"
									fontSize="16px"
									fontWeight="600"
									textColor={
										hasOptionEnabled === false ? "gray.400" : undefined
									}
									className={
										showError && hasOptionEnabled && isValueMissing
											? "text-red-500"
											: ""
									}
								>
									{variant.label}
								</Text>
								{label && !isExpanded && (
									<>
										{isDifferentBetweenSides ? (
											<Tooltip content="Pour votre information: cette option n'est pas la même des deux côtés">
												<Text
													lineHeight="21px"
													fontSize="16px"
													fontWeight="600"
													color="orange.500"
												>
													{label}
												</Text>
											</Tooltip>
										) : (
											<Text
												lineHeight="21px"
												fontSize="16px"
												fontWeight="600"
												color="brand.700"
											>
												{label}
											</Text>
										)}
									</>
								)}
								{hasOptionEnabled && variant.description && (
									<Tooltip content={variant.description}>
										<CircleHelpIcon color="brand.600" />
									</Tooltip>
								)}
							</Flex>
							{hasOptionEnabled && <AccordionIcon />}
						</Flex>
					</AccordionButton>
					<AccordionPanel>
						{isExpanded && variantImageUrl && (
							<Flex justifyContent="center" pb={4} pt={1}>
								<img
									src={variantImageUrl}
									alt={variantImageAlt}
									className="max-w-full h-auto"
								/>
							</Flex>
						)}
						<EarSectionFormSection
							type={
								variant.label.startsWith("Matière") ? "MATERIAL" : variant.type
							}
							options={variant.options.data.map((option) => ({
								key: option.uuid,
								label: option.label,
								value: option.uuid,
								disabled: isDisabled(variant, option, getForm(side)),
								fileId: option.fileId,
							}))}
							value={{
								label:
									getVariantValue(side, variant.id.toString())?.label ?? "",
								value: getVariantValue(side, variant.id.toString())?.uuid ?? "",
							}}
							setValue={(option: TSelectDropdownOption) => {
								const value = variant.options.data.find(
									(v) => v.uuid === option.value,
								);
								if (value) {
									setVariantValue(side, variant.id.toString(), value);
									autoFormIndex();
								}
							}}
						/>
					</AccordionPanel>
				</>
			)}
		</AccordionItem>
	);
};
